import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, GitCompareArrows, X } from "lucide-react";
import { createChart, CandlestickSeries } from "lightweight-charts";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { BIST_QUICK } from "@/lib/quickSymbols";
import { useTheme } from "@/contexts/ThemeContext";

const PERIODS = [
  { id: "1m", label: "1A" },
  { id: "3m", label: "3A" },
  { id: "6m", label: "6A" },
  { id: "1y", label: "1Y" },
];

export default function ComparePage() {
  const [sp, setSp] = useSearchParams();
  const [a, setA] = useState((sp.get("a") || "AKBNK").toUpperCase());
  const [b, setB] = useState((sp.get("b") || "GARAN").toUpperCase());
  const [period, setPeriod] = useState(sp.get("period") || "1y");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    if (!a || !b) { toast.error("İki hisse seçin"); return; }
    setLoading(true);
    setSp({ a, b, period });
    try {
      const { data } = await api.get(`/compare?symbols=${a},${b}&period=${period}`);
      setData(data);
    } catch { toast.error("Karşılaştırma yüklenemedi"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="space-y-6" data-testid="compare-page">
      <button onClick={() => nav(-1)} className="flex items-center gap-1.5 text-sm text-text-secondary hover:text-brand-blue">
        <ArrowLeft size={14} /> Geri
      </button>

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-text-primary ztan-title flex items-center gap-2">
            <GitCompareArrows size={22} className="text-brand-blue" /> Hisse Karşılaştırma
          </h1>
          <p className="text-sm text-text-secondary mt-1">İki BIST hissesini yan yana inceleyin</p>
        </div>
      </div>

      <div className="bg-bg-panel border border-bg-border rounded-xl p-4 flex flex-wrap items-center gap-3">
        <SymbolInput label="Hisse A" value={a} onChange={setA} testId="compare-input-a" />
        <SymbolInput label="Hisse B" value={b} onChange={setB} testId="compare-input-b" />
        <div className="flex items-center gap-1.5 bg-bg-base p-1 rounded-lg border border-bg-border">
          {PERIODS.map((p) => (
            <button
              key={p.id}
              onClick={() => setPeriod(p.id)}
              className={`px-3 py-1.5 text-xs rounded-md ${
                period === p.id ? "bg-brand-blue text-white" : "text-text-secondary hover:text-text-primary"
              }`}
            >{p.label}</button>
          ))}
        </div>
        <button
          data-testid="compare-run-btn"
          onClick={load}
          className="bg-accent-yellow text-bg-base font-semibold px-4 py-2 rounded-lg text-sm hover:opacity-90"
        >Karşılaştır</button>
      </div>

      {loading ? (
        <div className="p-16 flex justify-center"><div className="radar-loader" /></div>
      ) : data && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {data.items.map((it, i) => <CompareCard key={i} item={it} />)}
        </div>
      )}

      {data && data.items.length === 2 && (
        <IndicatorCompareTable a={data.items[0]} b={data.items[1]} />
      )}
    </div>
  );
}

function SymbolInput({ label, value, onChange, testId }) {
  const [open, setOpen] = useState(false);
  const q = (value || "").toUpperCase();
  const list = BIST_QUICK.filter((s) => s.startsWith(q)).slice(0, 8);
  return (
    <div className="relative">
      <label className="block text-[10px] uppercase tracking-wider text-text-secondary mb-1">{label}</label>
      <input
        data-testid={testId}
        value={value}
        onChange={(e) => { onChange(e.target.value.toUpperCase()); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className="bg-bg-base border border-bg-border text-text-primary font-mono rounded-lg px-3 py-2 text-sm w-32 focus:border-brand-blue focus:outline-none"
      />
      {open && list.length > 0 && q !== list[0] && (
        <div className="absolute z-20 mt-1 w-32 bg-bg-panel border border-bg-border rounded-lg shadow-2xl overflow-hidden">
          {list.map((s) => (
            <button
              key={s}
              onMouseDown={() => onChange(s)}
              className="block w-full text-left px-3 py-1.5 text-xs font-mono text-text-primary hover:bg-bg-hover"
            >{s}</button>
          ))}
        </div>
      )}
    </div>
  );
}

function CompareCard({ item }) {
  const containerRef = useRef(null);
  const { theme } = useTheme();
  const ind = item.indicators || {};
  useEffect(() => {
    if (!containerRef.current || !item.bars?.length) return;
    const isDark = theme !== "light";
    const chart = createChart(containerRef.current, {
      layout: { background: { color: isDark ? "#162442" : "#FFFFFF" }, textColor: isDark ? "#9AA8C7" : "#5A6B92", fontFamily: "Inter" },
      grid: { vertLines: { color: isDark ? "#1D2F55" : "#EEF3FB" }, horzLines: { color: isDark ? "#1D2F55" : "#EEF3FB" } },
      rightPriceScale: { borderColor: isDark ? "#2A3D66" : "#D8E0F0" },
      timeScale: { borderColor: isDark ? "#2A3D66" : "#D8E0F0" },
      localization: { locale: "tr-TR" },
      autoSize: true,
    });
    const s = chart.addSeries(CandlestickSeries, {
      upColor: "#46C06F", downColor: "#E5484D",
      borderUpColor: "#46C06F", borderDownColor: "#E5484D",
      wickUpColor: "#46C06F", wickDownColor: "#E5484D",
    });
    s.setData(item.bars.map((b) => ({ time: b.time, open: b.open, high: b.high, low: b.low, close: b.close })));
    chart.timeScale().fitContent();
    return () => chart.remove();
  }, [item, theme]);

  if (item.error) {
    return (
      <div className="bg-bg-panel border border-bg-border rounded-xl p-6 text-center">
        <div className="text-signal-down font-semibold">{item.symbol}</div>
        <div className="text-sm text-text-secondary mt-2">{item.error}</div>
      </div>
    );
  }

  const posClass = (v) => (v == null ? "text-text-primary" : v >= 0 ? "text-signal-up" : "text-signal-down");
  return (
    <div className="bg-bg-panel border border-bg-border rounded-xl overflow-hidden">
      <div className="p-4 border-b border-bg-border flex items-start justify-between">
        <div>
          <div className="text-2xl font-black font-mono text-text-primary">{item.symbol}</div>
          <div className="text-xs text-text-secondary">{item.name}</div>
        </div>
        <div className="text-right">
          <div className="text-lg font-bold font-mono text-text-primary">{fmt(ind.close)}₺</div>
          <div className={`text-xs font-mono ${posClass(ind.pct_change)}`}>{ind.pct_change > 0 ? "+" : ""}{fmt(ind.pct_change)}%</div>
        </div>
      </div>
      <div ref={containerRef} className="h-64 w-full" />
    </div>
  );
}

function IndicatorCompareTable({ a, b }) {
  const ai = a.indicators || {}, bi = b.indicators || {};
  const rows = [
    { label: "Son Fiyat (₺)", av: ai.close, bv: bi.close },
    { label: "Günlük % Değişim", av: ai.pct_change, bv: bi.pct_change, pct: true },
    { label: "RSI (14)", av: ai.rsi, bv: bi.rsi, dec: 1 },
    { label: "MACD Durumu", av: sLabel(ai.macd_status), bv: sLabel(bi.macd_status), str: true },
    { label: "EMA 20/50", av: sLabel(ai.ma_status), bv: sLabel(bi.ma_status), str: true },
    { label: "Bollinger", av: bbLabel(ai.bb_status), bv: bbLabel(bi.bb_status), str: true },
    { label: "EMA 20", av: ai.ema20, bv: bi.ema20 },
    { label: "EMA 50", av: ai.ema50, bv: bi.ema50 },
    { label: "Hacim Katı", av: ai.volume_ratio, bv: bi.volume_ratio, ratio: true },
  ];
  return (
    <div className="bg-bg-panel border border-bg-border rounded-xl overflow-hidden">
      <div className="p-4 border-b border-bg-border">
        <h3 className="text-text-primary font-semibold">İndikatör Karşılaştırması</h3>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-text-secondary text-xs uppercase tracking-wider border-b border-bg-border">
            <th className="py-3 px-4 text-left">Metrik</th>
            <th className="py-3 px-4 text-right font-mono">{a.symbol}</th>
            <th className="py-3 px-4 text-right font-mono">{b.symbol}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.label} className="border-b border-bg-border">
              <td className="py-2.5 px-4 text-text-secondary">{r.label}</td>
              <td className={`py-2.5 px-4 text-right font-mono ${cellCls(r)}`}>{cellVal(r.av, r)}</td>
              <td className={`py-2.5 px-4 text-right font-mono ${cellCls(r, true)}`}>{cellVal(r.bv, r)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function cellCls(r, isB) {
  if (r.pct) {
    const v = isB ? r.bv : r.av;
    if (v == null) return "text-text-primary";
    return v >= 0 ? "text-signal-up" : "text-signal-down";
  }
  return "text-text-primary";
}
function cellVal(v, r) {
  if (v == null) return "-";
  if (r.str) return v;
  if (r.pct) return `${v > 0 ? "+" : ""}${Number(v).toFixed(2)}%`;
  if (r.ratio) return `${Number(v).toFixed(2)}x`;
  return Number(v).toLocaleString("tr-TR", { minimumFractionDigits: r.dec ?? 2, maximumFractionDigits: r.dec ?? 2 });
}
function fmt(v, d = 2) { if (v == null) return "-"; return Number(v).toLocaleString("tr-TR", { minimumFractionDigits: d, maximumFractionDigits: d }); }
function sLabel(s) {
  return ({ golden_cross: "Altın Kesişim", death_cross: "Ölüm Kesişimi", bullish: "Yükseliş", bearish: "Düşüş" })[s] || "-";
}
function bbLabel(s) {
  return ({ upper_break: "Üst Kırılım", lower_break: "Alt Kırılım", inside: "Bant İçinde" })[s] || "-";
}
